import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { links, type ShareLink } from '../api'
import { formatDate } from '../format'

export default function ShareLinks() {
  const queryClient = useQueryClient()
  const list = useQuery({ queryKey: ['links'], queryFn: links.list })
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const revoke = useMutation({
    mutationFn: (id: string) => links.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['links'] }),
  })

  async function copy(link: ShareLink) {
    await navigator.clipboard.writeText(link.url)
    setCopiedId(link.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="pad">
      <h1>공유 링크</h1>
      <p className="muted small">
        계정 없는 사람에게 파일 하나를 건네거나, 그 사람에게서 파일을 받는 주소입니다. 파일
        목록에서 파일 행의 “공유 링크”, 폴더 행의 “업로드 링크”로 만듭니다.
      </p>

      <table className="entries">
        <thead>
          <tr>
            <th>대상</th>
            <th>조건</th>
            <th className="num">쓴 횟수</th>
            <th className="num">만료</th>
            <th className="actions" />
          </tr>
        </thead>
        <tbody>
          {list.data?.length === 0 && (
            <tr>
              <td colSpan={5} className="muted center">
                만든 링크가 없습니다.
              </td>
            </tr>
          )}
          {list.data?.map((link) => (
            <tr key={link.id} className={linkStatus(link) === '유효' ? '' : 'muted'}>
              <td>
                {link.kind === 'upload' ? '⬆ ' : '⬇ '}
                {link.filename}
                <br />
                <span className="mono small muted">{link.path}</span>
              </td>
              <td className="small">
                {link.kind === 'upload' ? '받는 링크' : '주는 링크'}
                {link.hasPassword ? ' · 비밀번호 있음' : ' · 비밀번호 없음'}
                {link.maxUses !== null && ` · 최대 ${link.maxUses}${link.kind === 'upload' ? '개' : '회'}`}
                <br />
                <span className={linkStatus(link) === '유효' ? 'muted' : 'error'}>{linkStatus(link)}</span>
              </td>
              <td className="num">{link.useCount}</td>
              <td className="num small">{link.expiresAt ? formatDate(link.expiresAt) : '없음'}</td>
              <td className="actions">
                <button type="button" className="link" onClick={() => void copy(link)}>
                  {copiedId === link.id ? '복사됨' : '주소 복사'}
                </button>
                {!link.revokedAt && (
                  <button
                    type="button"
                    className="link danger"
                    onClick={() => {
                      if (!window.confirm('이 링크를 취소할까요? 받은 사람은 더 이상 못 받습니다.')) return
                      revoke.mutate(link.id)
                    }}
                  >
                    취소
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function linkStatus(link: ShareLink): string {
  if (link.revokedAt) return '취소됨'
  if (link.expiresAt && new Date(link.expiresAt).getTime() < Date.now()) return '만료됨'
  if (link.maxUses !== null && link.useCount >= link.maxUses) return '다 씀'
  return '유효'
}
